import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { compactNaira, naira } from "../../lib/format";
import { HC, DEPT_COLORS, label } from "./theme";
import { Card, SectionHead, Pill, Btn, Table, Avatar, Empty } from "./ui";
import WorkforceIntelligence from "./WorkforceIntelligence";
import FloorBoard from "./FloorBoard";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import RoleDashboard from "../../components/RoleDashboard";
import {
  Users, CheckCircle2, Clock, UserX, UsersRound, Banknote, ArrowUp, ArrowDown,
  ArrowRight, UserPlus, CalendarCheck, CalendarPlus, Calculator, FileText, BarChart3,
  CalendarDays, Sunrise, LogOut as LogOutIcon, Plane, AlarmClock, AlarmClockOff, FileCheck2,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

const KPI_META = [
  { key: "total_employees", label: "Total Employees", Icon: Users, color: HC.burgundy, tint: HC.burgundySoft, kind: "count" },
  { key: "present_today", label: "Present Today", Icon: CheckCircle2, color: HC.green, tint: HC.greenTint, kind: "pct" },
  { key: "late_today", label: "Late Today", Icon: Clock, color: HC.amber, tint: HC.amberTint, kind: "pct" },
  { key: "absent_today", label: "Absent Today", Icon: UserX, color: HC.red, tint: HC.redTint, kind: "pct" },
  { key: "on_shift_now", label: "On Shift Now", Icon: UsersRound, color: HC.purple, tint: HC.purpleTint, kind: "count" },
  { key: "monthly_payroll_cost", label: "Monthly Payroll Cost", Icon: Banknote, color: HC.gold, tint: HC.goldSoft, kind: "money" },
];

// Every KPI card now links to the module that owns the detail. Query params
// preserve today's date so the destination lands on pre-filtered data.
const _today = new Date().toISOString().slice(0, 10);
const KPI_TARGET = {
  total_employees:      "/hr/employees",
  present_today:        `/hr/attendance?date=${_today}&status=present`,
  late_today:           `/hr/attendance?date=${_today}&status=late`,
  absent_today:         `/hr/attendance?date=${_today}&status=absent`,
  on_shift_now:         `/hr/shifts?date=${_today}`,
  monthly_payroll_cost: "/hr/payroll",
};

const EX_ICON = {
  late: AlarmClock, early_departure: LogOutIcon, absent: UserX,
  on_leave: Plane, missed_clock_in: AlarmClockOff, missed_clock_out: AlarmClockOff,
};
const EX_TONE = {
  late: [HC.amber, HC.amberTint], early_departure: [HC.blue, HC.blueTint], absent: [HC.red, HC.redTint],
  on_leave: [HC.purple, HC.purpleTint], missed_clock_in: [HC.soft, HC.borderSoft], missed_clock_out: [HC.soft, HC.borderSoft],
};

const QUICK = [
  { label: "Add Employee", Icon: UserPlus, to: "/hr/employees?new=1" },
  { label: "Record Attendance", Icon: CalendarCheck, to: "/hr/attendance?record=1" },
  { label: "Create Shift", Icon: CalendarPlus, to: "/hr/shifts?new=1" },
  { label: "Calculate Payroll", Icon: Calculator, to: "/hr/payroll" },
  { label: "Generate Payslips", Icon: FileText, to: "/hr/payslips" },
  { label: "Reports", Icon: BarChart3, to: "/hr/reports" },
];

function Delta({ value, pct, invert, suffix = "" }) {
  if (value === null || value === undefined) return <span className="text-[11px]" style={{ color: HC.soft }}>—</span>;
  const up = value > 0;
  const good = invert ? !up : up;
  const c = value === 0 ? HC.soft : good ? HC.green : HC.red;
  const Ico = up ? ArrowUp : ArrowDown;
  return (
    <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold" style={{ color: c }}>
      {value !== 0 && <Ico size={12} />}{pct ? `${Math.abs(value).toFixed(1)}%` : Math.abs(value)}{suffix}
    </span>
  );
}

const FULL_VIEW_ROLES = ["General Manager", "Board/MD", "IT"];

export default function HRDashboard() {
  const { user } = useAuth();
  if (user && !user.is_admin && !FULL_VIEW_ROLES.includes(user.role_name)) {
    return <RoleDashboard domain="hr" heading="HR & Admin Dashboard" />;
  }
  return <HRDashboardFull />;
}

function HRDashboardFull() {
  const { locationId } = useOutletContext();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [range, setRange] = useState(7);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/hr/dashboard", { params: { location_id: locationId || undefined, days: range } })
    .then(setD).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [locationId, range]);

  const payroll = d?.current_payroll;
  const dept = d?.headcount_by_department || [];
  const total = d?.kpis?.total_employees?.value || 0;

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/hr/payroll/runs/${payroll.id}/approve`, {});
      toast.success("Payroll approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!reason.trim()) return toast.error("A rejection reason is required");
    setBusy(true);
    try {
      await api.post(`/hr/payroll/runs/${payroll.id}/reject`, { reason });
      toast.success("Payroll rejected"); setRejectOpen(false); setReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const kpiValue = (m, k) => {
    if (m.kind === "money") return compactNaira(k?.value);
    return k?.value ?? 0;
  };

  return (
    <div className="space-y-5" data-testid="hr-dashboard">
      {/* KPI ROW */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-3.5">
        {KPI_META.map((m) => {
          const k = d?.kpis?.[m.key];
          const target = KPI_TARGET[m.key];
          return (
            <button
              type="button"
              key={m.key}
              onClick={() => target && navigate(target)}
              data-testid={`hr-kpi-${m.key}-link`}
              className="text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 rounded-2xl"
            >
              <Card testid={`hr-kpi-${m.key}`} className="!p-4 hover:shadow-md hover:border-[#C49A4A] transition-shadow">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: m.tint, color: m.color }}>
                  <m.Icon size={19} />
                </span>
                <div className="min-w-0">
                  <div className="text-[12px] font-semibold leading-tight" style={{ color: HC.muted }}>{m.label}</div>
                  <div className="font-brand font-bold text-[30px] leading-none mt-1 tabular-nums" style={{ color: HC.text }}>
                    {kpiValue(m, k)}
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t" style={{ borderColor: HC.borderSoft }}>
                <div className="text-[11.5px]" style={{ color: HC.soft }}>
                  {m.kind === "pct" ? `${k?.pct ?? 0}% of total` : k?.sub || "—"}
                </div>
                <div className="mt-1 flex items-center gap-1.5">
                  {m.kind === "money"
                    ? <Delta value={k?.delta_pct} pct invert={false} />
                    : <Delta value={k?.delta} invert={m.key === "late_today" || m.key === "absent_today"} />}
                  <span className="text-[11px]" style={{ color: HC.soft }}>{k?.delta_label}</span>
                </div>
              </div>
              </Card>
            </button>
          );
        })}
      </div>

      {/* LIVE FLOOR */}
      <FloorBoard locationId={locationId} onOpenEmployee={(p) => navigate(`/hr/attendance?employee=${p.employee_id}`)} />

      {/* AI */}
      <WorkforceIntelligence locationId={locationId} />

      {/* CHARTS + PAYROLL */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <Card testid="hr-attendance-overview" className="xl:col-span-5">
          <SectionHead title="Attendance Overview" sub={`Last ${range} days`}
            right={
              <select data-testid="hr-attendance-range" value={range} onChange={(e) => setRange(Number(e.target.value))}
                className="h-9 px-3 rounded-xl text-[12.5px] font-semibold bg-white outline-none"
                style={{ border: `1px solid ${HC.border}`, color: HC.text }}>
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            } />
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d?.attendance_trend || []} barSize={26}>
                <CartesianGrid vertical={false} stroke={HC.borderSoft} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: HC.soft }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: HC.soft }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: `1px solid ${HC.border}`, fontSize: 12 }} />
                <Bar dataKey="absent" stackId="a" fill={HC.red} radius={[0, 0, 4, 4]} name="Absent" />
                <Bar dataKey="late" stackId="a" fill={HC.gold} name="Late" />
                <Bar dataKey="present" stackId="a" fill={HC.green} radius={[4, 4, 0, 0]} name="Present" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-5 mt-2">
            {[["Present", HC.green], ["Late", HC.gold], ["Absent", HC.red]].map(([t, c]) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-[11.5px]" style={{ color: HC.muted }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />{t}
              </span>
            ))}
          </div>
        </Card>

        <Card testid="hr-headcount-department" className="xl:col-span-4">
          <SectionHead title="Headcount by Department" sub="Active workforce" />
          <div className="flex items-center gap-4">
            <div className="relative w-[190px] h-[190px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={dept} dataKey="value" nameKey="name" innerRadius={58} outerRadius={90} paddingAngle={1} stroke="none">
                    {dept.map((_, i) => <Cell key={i} fill={DEPT_COLORS[i % DEPT_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: `1px solid ${HC.border}`, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-brand font-bold text-3xl" style={{ color: HC.text }}>{total}</div>
                <div className="text-[11px]" style={{ color: HC.soft }}>Total</div>
              </div>
            </div>
            <div className="flex-1 space-y-2 min-w-0">
              {dept.length ? dept.map((x, i) => (
                <div key={x.name} className="flex items-center gap-2 text-[12.5px]">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DEPT_COLORS[i % DEPT_COLORS.length] }} />
                  <span className="truncate" style={{ color: HC.text }}>{x.name}</span>
                  <span className="flex-1" />
                  <span className="font-semibold tabular-nums" style={{ color: HC.text }}>{x.value}</span>
                  <span className="tabular-nums" style={{ color: HC.soft }}>({x.pct}%)</span>
                </div>
              )) : <Empty text="No department data" />}
            </div>
          </div>
        </Card>

        <Card testid="hr-current-payroll" className="xl:col-span-3">
          <SectionHead title="Current Payroll"
            right={<button className="text-[12px] font-semibold" style={{ color: HC.burgundy }}
              onClick={() => navigate("/hr/payroll")} data-testid="hr-payroll-viewall">View all</button>} />
          {payroll ? (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="font-brand font-bold text-[17px]" style={{ color: HC.text }}>{payroll.period_label}</div>
                <Pill status={payroll.status} testid="hr-payroll-status" />
              </div>
              <div className="mt-3 space-y-2.5 text-[12.5px]">
                {[
                  ["Payroll Period", `${payroll.date_from} – ${payroll.date_to}`],
                  ["Employees", payroll.employees],
                  ["Gross Payroll", naira(payroll.gross)],
                  ["Total Deductions", naira(payroll.deductions), HC.red],
                  ["Net Payroll", naira(payroll.net), HC.green],
                ].map(([k, v, c]) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <span style={{ color: HC.muted }}>{k}</span>
                    <span className="font-semibold text-right tabular-nums" style={{ color: c || HC.text }}>{v}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-3">
                  <span style={{ color: HC.muted }}>Approval Status</span>
                  <Pill status={payroll.status} text={payroll.approval_status} />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span style={{ color: HC.muted }}>Next Action</span>
                  <span className="font-semibold" style={{ color: HC.text }}>{payroll.next_action}</span>
                </div>
              </div>
              <Btn className="w-full mt-4" data-testid="hr-review-payroll"
                onClick={() => navigate(`/hr/payroll?run=${payroll.id}`)}>
                Review Payroll <ArrowRight size={15} />
              </Btn>
            </>
          ) : <Empty text="No payroll run yet" />}
        </Card>
      </div>

      {/* SHIFTS + EXCEPTIONS + QUICK ACTIONS */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <Card testid="hr-upcoming-shifts" className="xl:col-span-5">
          <SectionHead title="Upcoming Shifts" sub="Next scheduled rota"
            right={<button className="text-[12px] font-semibold" style={{ color: HC.burgundy }}
              onClick={() => navigate("/hr/shifts")} data-testid="hr-view-schedule">View schedule</button>} />
          <Table
            testid="hr-shifts-table"
            empty="No upcoming shifts"
            rows={(d?.upcoming_shifts || []).slice(0, 6)}
            columns={[
              { key: "employee_name", label: "EMPLOYEE", render: (r) => (
                  <span className="flex items-center gap-2"><Avatar name={r.employee_name} /> <span className="font-medium">{r.employee_name}</span></span>) },
              { key: "position", label: "POSITION" },
              { key: "location", label: "LOCATION" },
              { key: "shift", label: "SHIFT", render: (r) => <Pill status={r.shift === "Evening" ? "on_leave" : r.shift === "Afternoon" ? "late" : "present"} text={r.shift} /> },
              { key: "time", label: "TIME", render: (r) => <span className="tabular-nums text-[12.5px]">{r.time}</span> },
              { key: "status", label: "STATUS", render: (r) => <Pill status={r.status} /> },
            ]}
          />
          <button onClick={() => navigate("/hr/shifts")} className="w-full mt-3 inline-flex items-center justify-center gap-2 text-[12.5px] font-semibold"
            style={{ color: HC.burgundy }}>View full schedule <ArrowRight size={14} /></button>
        </Card>

        <Card testid="hr-exceptions" className="xl:col-span-4">
          <SectionHead title="Attendance Exceptions" sub="Today"
            right={<button className="text-[12px] font-semibold" style={{ color: HC.burgundy }}
              onClick={() => navigate("/hr/attendance")} data-testid="hr-exceptions-viewall">View all</button>} />
          <div className="space-y-1.5">
            {(d?.exceptions || []).map((ex) => {
              const Ico = EX_ICON[ex.code] || AlarmClock;
              const [c, bg] = EX_TONE[ex.code] || [HC.soft, HC.borderSoft];
              return (
                <button key={ex.code} data-testid={`hr-exception-${ex.code}`}
                  onClick={() => navigate(`/hr/attendance?status=${ex.code === "late" ? "late" : ex.code === "absent" ? "absent" : ex.code === "on_leave" ? "on_leave" : ""}`)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-xl text-left hover:bg-[#FBF8F2] transition-colors">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: bg, color: c }}>
                    <Ico size={16} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold" style={{ color: HC.text }}>{ex.label}</div>
                    <div className="text-[11.5px]" style={{ color: HC.soft }}>{ex.count} employee{ex.count === 1 ? "" : "s"} today</div>
                  </div>
                  <span className="flex-1" />
                  <ArrowRight size={14} style={{ color: HC.soft }} />
                </button>
              );
            })}
          </div>
        </Card>

        <div className="xl:col-span-3 space-y-4">
          <Card testid="hr-quick-actions">
            <SectionHead title="Quick Actions" />
            <div className="grid grid-cols-3 gap-2.5">
              {QUICK.map((q) => (
                <button key={q.label} data-testid={`hr-quick-${q.label.toLowerCase().replace(/ /g, "-")}`}
                  onClick={() => navigate(q.to)}
                  className="rounded-xl border p-3 flex flex-col items-center gap-2 hover:bg-[#FBF8F2] transition-colors"
                  style={{ borderColor: HC.border }}>
                  <q.Icon size={18} style={{ color: HC.gold }} />
                  <span className="text-[10.5px] font-semibold text-center leading-tight" style={{ color: HC.text }}>{q.label}</span>
                </button>
              ))}
            </div>
          </Card>
          <Card testid="hr-latest-payslips">
            <SectionHead title="Latest Payslip Generated"
              right={<button className="text-[12px] font-semibold" style={{ color: HC.burgundy }}
                onClick={() => navigate("/hr/payslips")}>View all</button>} />
            {d?.latest_payslip_batch ? (
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: HC.greenTint, color: HC.green }}>
                  <FileCheck2 size={19} />
                </span>
                <div>
                  <div className="text-[13px] font-semibold" style={{ color: HC.text }}>{d.latest_payslip_batch.period} Payroll</div>
                  <div className="text-[11.5px]" style={{ color: HC.soft }}>
                    {d.latest_payslip_batch.count} payslips generated
                  </div>
                </div>
              </div>
            ) : <Empty text="No payslips generated yet" />}
          </Card>
        </div>
      </div>

      {/* APPROVAL REMINDER */}
      {payroll?.status === "pending_approval" && (
        <Card testid="hr-approval-reminder" className="!p-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: HC.burgundySoft, color: HC.burgundy }}>
              <CalendarDays size={22} />
            </span>
            <div className="min-w-0">
              <div className="font-brand font-bold text-[16px]" style={{ color: HC.text }}>Payroll Approval Reminder</div>
              <div className="text-[12.5px]" style={{ color: HC.soft }}>
                {payroll.period_label} is pending your approval.
              </div>
            </div>
            <div className="hidden lg:block w-px h-10" style={{ background: HC.border }} />
            <div>
              <div className="text-[10px] font-bold tracking-[0.14em]" style={{ color: HC.soft }}>PENDING SINCE</div>
              <div className="text-[13px] font-semibold" style={{ color: HC.text }}>
                {payroll.submitted_at ? new Date(payroll.submitted_at).toLocaleString("en-NG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-[0.14em] mb-1" style={{ color: HC.soft }}>APPROVERS</div>
              <div className="flex items-center gap-1">
                {(payroll.approvers || []).slice(0, 2).map((a, i) => (
                  <span key={i} className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold"
                    style={{ background: HC.borderSoft, color: HC.muted }}>{a.initials || (a.name || "?").slice(0, 2).toUpperCase()}</span>
                ))}
                {(payroll.approvers || []).length > 2 && (
                  <span className="text-[11px] font-semibold" style={{ color: HC.soft }}>+{payroll.approvers.length - 2}</span>
                )}
              </div>
            </div>
            <span className="flex-1" />
            <div className="flex items-center gap-2.5">
              <Btn onClick={approve} disabled={busy} data-testid="hr-approve-payroll">Approve Payroll</Btn>
              <Btn variant="outline" onClick={() => setRejectOpen(true)} data-testid="hr-reject-payroll">Reject</Btn>
            </div>
          </div>
        </Card>
      )}

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject payroll</DialogTitle>
            <DialogDescription>A reason is mandatory and is stored in the audit trail.</DialogDescription>
          </DialogHeader>
          <textarea data-testid="hr-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            rows={4} placeholder="Why is this payroll being rejected?"
            className="w-full p-3 rounded-xl text-sm outline-none" style={{ border: `1px solid ${HC.border}` }} />
          <DialogFooter>
            <Btn variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={reject} disabled={busy} data-testid="hr-confirm-reject">Reject Payroll</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
